import { DateTime, Duration } from 'luxon';
import { DateRange, MaybeArray, UnwrapArray } from '../models';

export const noop = () => {};
export const noopAsync = async () => {};

//#region Math functions
/**
 * Lets you limit a value within a specified range.
 *
 * @param value - A number that will be returned as long as the provided value
 *   is in range between `min` and `max`.
 * @param min - A number which will be returned when provided `value` is lower
 *   than `min`.
 * @param max - A number which will be returned when provided `value` is higher
 *   than `max`.
 * @returns A number between min and max bounds.
 */
export function clamp(value: number, min: number, max: number) {
  'worklet';
  return Math.min(Math.max(value, min), max);
}

/**
 * Check if a number has a specific flag set using bitwise AND operation
 * @param value Number to check
 * @param flag Flag to check against
 * @example hasFlag(7, 2) // returns true (0000 0111 & 0000 0010 = 0000 0010)
 * @returns True if the flag is set, false otherwise
 */
export function hasFlag(value: number, flag: number): boolean {
  if (flag === 0) {
    return false;
  }

  return (value & flag) === flag;
}

/**
 * Check if a number has any of the given combined flags set using bitwise AND
 * operation
 * @param value Number to check
 * @param flags Combined flags to check against
 * @example hasAnyFlag(7, 8 | 2) // returns true (0000 0111 & 0000 1010 = 0000 0010)
 * @returns True if any of the flags is set, false otherwise
 */
export function hasAnyFlag(value: number, flags: number): boolean;
export function hasAnyFlag(value: number, flags: number[]): boolean;
export function hasAnyFlag(value: number, flags: number | number[]): boolean {
  const mask = typeof flags === 'number' ? flags : combineFlags(flags);

  if (mask === 0) {
    return false;
  }

  return (value & mask) !== 0;
}

/**
 * Add a flag to a number using bitwise OR operation
 */
export function addFlag(value: number, flag: number): number {
  return value | flag;
}

export function addFlags(value: number, flags: number[]): number {
  return flags.reduce((result, value) => addFlag(result, value), value);
}

export function combineFlags(flags: number[]) {
  return addFlags(0, flags);
}

/**
 * Remove a flag from a number using bitwise AND and NOT operations
 */
export function removeFlag(value: number, flag: number): number {
  return value & ~flag;
}

export function toggleFlag(value: number, flag: number): number {
  return hasFlag(value, flag) ? removeFlag(value, flag) : addFlag(value, flag);
}
//#endregion

//#region String functions
/**
 *
 * @param str Char to check
 * @returns True if char is upper or lower case letter, otherwise false
 */
export function isLetter(str: string): boolean {
  'worklet';
  const match = str.match(/[A-Z|a-z|Ü|ü|å|Å|ä|Ä|ö|Ö]/i);
  return str.length === 1 && match?.length === 1;
}

/**
 * Returns the given string with the first char of each word transformed to upper case
 * @param string The string to transform
 * @param options Options for transformation
 * @param options.sentenceCase If true, only capitalize the first letter of the string (not each word)
 * @returns Titlecase or sentence case string
 */
export function titleCase(string: string, options?: { sentenceCase?: boolean }): string {
  'worklet';

  if (options?.sentenceCase) {
    // Only capitalize the first letter of the entire string
    const chars = string.split('');
    for (let i = 0; i < chars.length; i++) {
      if (isLetter(chars[i])) {
        chars[i] = chars[i].toUpperCase();
        break;
      }
    }
    return chars.join('');
  }

  return string
    .split(' ')
    .map(word => {
      const chars = word.split('');

      for (let i = 0; i < chars.length; i++) {
        if (isLetter(chars[i])) {
          chars[i] = chars[i].toUpperCase();
          break;
        }
      }

      return chars.join('');
    })
    .join(' ');
}
//#endregion

//#region Object functions
/**
 * Check if object is defined and has a valid value
 */
export function isEmpty(obj?: any): boolean {
  if (isArray(obj)) {
    return obj.length === 0;
  }

  if (obj instanceof Date) {
    return false;
  }

  return !isDefined(obj) || (!Object.getOwnPropertyNames(obj).length && typeof obj !== 'number');
}

export function isDefined<T = any>(obj?: T): obj is NonNullable<T> {
  return (
    obj !== undefined &&
    obj !== null &&
    (typeof obj === 'string' ? obj !== '' : true) &&
    (typeof obj === 'number' ? !isNaN(obj) && isFinite(obj) : true)
  );
}

/**
 * Check if the given parameter is of object type
 * @param obj The object to check
 * @returns True if the given parameter is of object type
 */
export function isObject(obj?: any): obj is Record<string, unknown> {
  return typeof obj === 'object' && obj !== null;
}

export function isFunction(obj?: any): obj is (...args: any[]) => any {
  return typeof obj === 'function';
}

export function isPromise(obj?: any): obj is Promise<any> {
  return (
    !!obj &&
    (typeof obj === 'object' || typeof obj === 'function') &&
    typeof obj.then === 'function'
  );
}

export function isBoolean(obj?: any): obj is boolean {
  'worklet';
  return obj === true || obj === false;
}

export function isNumber(obj?: any): obj is number {
  'worklet';
  return typeof obj === 'number' && !isNaN(obj) && isFinite(obj);
}

export function isString(obj?: any): obj is string {
  'worklet';
  return typeof obj === 'string';
}

/**
 * Check if a setting value is true
 */
export function isTrue(obj?: string): boolean {
  'worklet';
  return obj?.toLowerCase() === 'true';
}

/**
 * Check if a setting value is false
 */
export function isFalse(obj: string): boolean {
  'worklet';
  return obj?.toLowerCase() === 'false';
}

/**
 * Deep equals implementation
 * @param a
 * @param b
 * @param skipProps Exclude all properties in skipProps array. This will also exclude the same properties in nested objects
 * @returns Returns true if all properties of the objects have equal values
 */
export function equals<T = any>(a: T, b: T, skipProps?: Array<keyof UnwrapArray<T>>): boolean {
  if (a === b) {
    return true;
  }

  if (!isObject(a) || !isObject(b)) {
    return a === b;
  }

  if (a instanceof Date) {
    return b instanceof Date ? a.valueOf() === b.valueOf() : false;
  }

  if (
    Object.keys(a).filter(key => !skipProps?.includes(key as any)).length !==
    Object.keys(b).filter(key => !skipProps?.includes(key as any)).length
  ) {
    return false;
  }

  // Compares all object properties or array entries
  return Object.keys(a).every(prop => {
    if (isArray(skipProps) && skipProps.includes(prop as keyof UnwrapArray<T>)) {
      return true;
    }

    if (!Object.prototype.hasOwnProperty.call(b, prop)) {
      return false;
    }

    return equals<any>(a[prop], b[prop], skipProps);
  });
}

/**
 * Deep copy function for TypeScript.
 * Based on deep-copy-ts https://github.com/erikvullings/deep-copy-ts
 * @param T Generic type of target/copied value.
 * @param target Target value to be copied.
 * @see Orginal source: ts-deepcopy https://github.com/ykdr2017/ts-deepcopy
 * @see Code pen https://codepen.io/erikvullings/pen/ejyBYg
 */
export function clone<T>(target: T, maxDepth = Infinity, currentDepth = 0): T {
  if (currentDepth > maxDepth || isNotClonable(target)) {
    return target;
  }

  if (target instanceof Date) {
    return new Date(target.getTime()) as any;
  }

  if (isArray(target)) {
    const cp = [] as any[];
    (target as unknown as any[]).forEach(v => cp.push(v));

    return cp.map(n => clone<any>(n, maxDepth, currentDepth++)) as any;
  }

  if (typeof target === 'object' && !isEmpty(target)) {
    try {
      const cp = { ...target } as Record<string, unknown>;
      Object.entries(cp).forEach(([key, value]) => {
        try {
          cp[key] = clone<any>(value, maxDepth, currentDepth++);
        } catch {
          // Use original value when not possible to clone
          cp[key] = value;
          // Cannot import Logger here, utils are used in config plugins
          console.debug('Error cloning object', { target });
        }
      });

      return cp as T;
    } catch {
      // Cannot import Logger here, utils are used in config plugins
      console.debug('Error cloning object', { target });
    }
  }

  return target;
}

export function isNotClonable(target: any): boolean {
  return (
    target === null ||
    target === undefined ||
    isPromise(target) ||
    (typeof target === 'object' && isFunction(target['subscribe'])) || // Observable
    isFunction(target.constructor?.prototype['createEmbeddedView']) // TemplateRef
  );
}

/**
 * Returns a new object with all properies from @a and @b null or undefined objects will be skipped
 * @param a target object. Result will be of this type
 * @param b properties from this object will overwrite those in target object
 * @param skipAddedProperties if true, returned object will only include properties present in a. if false, target object will also include properties not present in a
 */
export function merge<T = any>(
  a: T,
  b: any,
  skipAddedProperties = false,
  deleteRemovedProperties = false
): T {
  let target = clone<any>(a);

  if (!isDefined(target)) {
    target = {} as any;
  }

  if (isDefined(b)) {
    const keysToDelete: string[] = [];

    // Replace properties in a with the ones from b
    for (const [key, value] of Object.entries(target)) {
      if (isObject(value) && !isArray(value) && !(value instanceof Date)) {
        // Merge nested objects
        target[key] = merge(value, b[key], skipAddedProperties, deleteRemovedProperties);
      } else if (isArray(value) && isArray(b[key])) {
        // Merge arrays. Deleted indexes are removed in target object
        target[key] = merge(value, b[key], false, true);

        if (!isDefined(target[key])) {
          delete target[key];
        }
      } else {
        if (b.hasOwnProperty(key)) {
          // Value of key has been updated in b
          target[key] = b[key];
        } else if (deleteRemovedProperties) {
          // Value of key has been deleted in b
          keysToDelete.push(key);
        }
      }

      // Merging of nested object resulted in null value, or key does not exist in b
      if (deleteRemovedProperties && (!isDefined(target[key]) || !isDefined(b[key]))) {
        keysToDelete.push(key);
      }
    }

    // Delete properties marked for deletion
    if (isArray(target)) {
      // Delete entries from array target, key is array index
      target = target.filter((_item, index) => !keysToDelete.includes(index.toString()));
      // target.splice(Number(key), 1);
    } else {
      for (const key of keysToDelete) {
        delete target[key];
      }
    }

    // Only use property keys from a in target object
    if (skipAddedProperties) {
      return target;
    }

    // Add properties from b that don't exist in a
    for (const [key, value] of Object.entries(b).filter(([key]) => !target.hasOwnProperty(key))) {
      if (isObject(value)) {
        target[key] = merge(value, target[key]);
      } else {
        target[key] = value;
      }
    }
  } else if (deleteRemovedProperties) {
    // Used when merging nested objects/arrays
    target = null;
  }

  return target;
}

//#endregion

//#region Array functions
/**
 * Returns true if obj is array
 * @param obj The object to check
 */
export function isArray<T = any>(obj: any): obj is Array<T> {
  return Array.isArray(obj);
}

/**
 * Returns an array without duplicates
 */
export function union<T>(a: Array<T>, b: MaybeArray<T>): Array<T> {
  if (!isArray(a) || !isDefined(b)) {
    return isArray(a) ? a : isArray(b) ? [...(b as T[])] : isDefined(b) ? [b as T] : [];
  }

  const setB = isArray(b) ? new Set(b as Array<T>) : new Set([b as T]);

  return Array.from(new Set([...a, ...setB]));
}

/**
 * Returns array of objects in @a that are also present in @b
 * @param a
 * @param b
 * @returns Array<T>
 */
export function intersection<T>(a: Array<T>, b: MaybeArray<T>): Array<T> {
  const setB = isArray(b) ? new Set(b as Array<T>) : new Set([b as T]);

  return [...a].filter(value => setB.has(value));
}

/**
 * Returns array of objects in @a that are not present in @b
 * @param a
 * @param b
 */
export function difference<T>(a: Array<T>, b: MaybeArray<T>): Array<T> {
  const setB = isArray(b) ? new Set(b as Array<T>) : new Set([b as T]);

  return [...a].filter(value => !setB.has(value));
}

/**
 * Returns array of unique values of @array
 * @param array
 */
export function distinct<T>(array: Array<T>): Array<T> {
  return Array.from(new Set(array));
}

/**
 * Filters an array. Each object in the array will be unique by the provided key.
 * When there are duplicates, the first value in the array will be the value used in the reult returned
 * @param array
 * @param key key in T to use as equality check
 */
export function distinctByKey<T>(array: T[], key: keyof T): T[] {
  const distinctValues = distinct(array.map(item => item[key]));

  return distinctValues.map(value => array.find(item => item[key] === value)!).filter(Boolean);
}

/**
 * Compare values for sorting
 * @param a
 * @param b
 */
export function sortFn<T = any>(a: T, b: T, compareKey?: keyof T): number {
  let aValue: any, bValue: any;
  const type = typeof a === typeof b ? typeof a : 'string';

  if (!isDefined(a) && !isDefined(b)) {
    return 0;
  }

  if (!isDefined(a) || !isDefined(b)) {
    return !isDefined(a) ? -1 : 1;
  }

  switch (type) {
    case 'object':
      aValue = compareKey ? a[compareKey] : a.toString();
      bValue = compareKey ? b[compareKey] : b.toString();

      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.valueOf();
        bValue = bValue.valueOf();
      }

      return sortFn(aValue, bValue);

    case 'string':
      aValue = a.toString();
      bValue = b.toString();

      return aValue.localeCompare(bValue);

    default:
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
  }
}

/**
 * Compare values for sorting using the provider key
 * @param a
 * @param b
 */
export function sortByKey<T>(key: keyof T, order: 'asc' | 'desc' = 'asc') {
  return (a: T, b: T) => sortFn(order === 'asc' ? a : b, order === 'asc' ? b : a, key);
}

/**
 * Returns the minimum value in the input array
 * @param array
 * @param key key in T to use for comparison
 * @returns
 */
export function min<T>(arr: Array<T>, key?: keyof T): number {
  if (key) {
    return arr.reduce((min, obj) => (obj[key] < min[key] ? obj : min))[key] as number;
  } else {
    // If no key is provided, assume array is of numbers
    return arr.reduce((min, val) => (val < min ? val : min)) as number;
  }
}

/**
 * Returns the maximum value in the input array
 * @param array
 * @param key key in T to use for comparison
 * @returns
 */
export function max<T>(arr: Array<T>, key?: keyof T): number {
  if (key) {
    return arr.reduce((max, obj) => (obj[key] > max[key] ? obj : max))[key] as number;
  } else {
    // If no key is provided, assume array is of numbers
    return arr.reduce((max, val) => (val > max ? val : max)) as number;
  }
}

export function average<T>(
  arr: Array<T>,
  key?: { [K in keyof T]: T[K] extends number ? K : never }[keyof T]
): number {
  if (key) {
    return arr.reduce((sum, obj) => sum + (obj[key] as unknown as number), 0) / arr.length;
  } else {
    // If no key is provided, assume array is of numbers
    return arr.reduce((sum, val) => sum + (val as number), 0) / arr.length;
  }
}

/**
 * Returns input or creates an array from input
 */
export function arrayify<T>(input: MaybeArray<T>): Array<T> {
  return isArray(input) ? input : [input];
}

/**
 * Group array of objects by key
 */
export function groupBy<TValue, TKey extends keyof TValue>(
  array: Array<TValue>,
  key: TKey
): Map<TValue[TKey], TValue[]> {
  return array.reduce((map, obj) => {
    const value = obj[key];

    if (!map.has(value)) {
      map.set(value, []);
    }

    map.get(value)?.push(obj);

    return map;
  }, new Map<TValue[TKey], TValue[]>());
}

/**
 * Moves an item one index in an array to another.
 * @param array Array in which to move the item.
 * @param fromIndex Starting index of the item.
 * @param toIndex Index to which the item should be moved.
 */
export function moveItemInArray<T = any>(array: T[], fromIndex: number, toIndex: number) {
  'worklet';

  const from = clamp(fromIndex, 0, array.length - 1);
  const to = clamp(toIndex, 0, array.length - 1);

  if (from === to) {
    return;
  }

  const target = array[from];
  const delta = to < from ? -1 : 1;

  for (let i = from; i !== to; i += delta) {
    array[i] = array[i + delta];
  }

  array[to] = target;
}
/**
 * Moves an item from one array to another.
 * @param currentArray Array from which to transfer the item.
 * @param targetArray Array into which to put the item.
 * @param currentIndex Index of the item in its current array.
 * @param targetIndex Index at which to insert the item.
 */
export function transferArrayItem<T = any>(
  currentArray: T[],
  targetArray: T[],
  currentIndex: number,
  targetIndex: number
) {
  'worklet';

  const from = clamp(currentIndex, 0, currentArray.length - 1);
  const to = clamp(targetIndex, 0, targetArray.length);

  if (currentArray.length) {
    targetArray.splice(to, 0, currentArray.splice(from, 1)[0]);
  }
}
//#endregion

//#region Date functions
export function isDateRange(obj: any): obj is DateRange {
  return (
    isArray(obj) && obj.length === 2 && obj[0] instanceof DateTime && obj[1] instanceof DateTime
  );
}

export function today(): DateTime {
  return DateTime.now().startOf('day');
}

export function yesterday(): DateTime {
  return today().minus({ days: 1 });
}

export function startOfYear(): DateTime {
  return today().startOf('year');
}

export function isToday(date: DateTime): boolean;
export function isToday(date: Date): boolean;
export function isToday(date: string): boolean;
export function isToday(date: number): boolean;
export function isToday(date: DateTime | Date | string | number): boolean {
  const parsedDate = createDate(date);

  return parsedDate ? parsedDate.hasSame(today(), 'day') : false;
}

/**
 * Create a DateTime object from an ISO date string and clear the time component
 * @param timestamp
 * @returns DateTime
 */
export function createDate(timestamp?: DateTime): DateTime;
export function createDate(timestamp?: number): DateTime;
export function createDate(timestamp?: string): DateTime;
export function createDate(timestamp?: Date): DateTime;
export function createDate(timestamp?: number | string | Date | DateTime | null): DateTime;
export function createDate(timestamp?: number | string | Date | DateTime | null): DateTime | null {
  if (!timestamp) return today();

  return isNumber(timestamp)
    ? // number
      DateTime.fromMillis(timestamp).startOf('day')
    : timestamp instanceof Date
      ? // Date
        DateTime.fromJSDate(timestamp).startOf('day')
      : isString(timestamp)
        ? // string
          DateTime.fromISO(timestamp).startOf('day')
        : DateTime.isDateTime(timestamp)
          ? // DateTime
            timestamp.startOf('day')
          : null;
}

/**
 * Returns true if the date has a value for hour, minute or second
 */
export function hasTime(date: string): boolean {
  const dateTime = DateTime.fromISO(date);

  return dateTime.hour > 0 || dateTime.minute > 0 || dateTime.second > 0;
}

/**
 * Returns the number of months between two dates
 */
export function getDuration(dateRange: DateRange): Duration;
export function getDuration(startDate: DateTime, endDate: DateTime): Duration;
export function getDuration(startParam: DateTime | DateRange, endParam?: DateTime): Duration {
  let startDate: DateTime, endDate: DateTime;

  if (isArray(startParam)) {
    startDate = startParam[0];
    endDate = startParam[1];
  } else {
    startDate = startParam;
    endDate = endParam!;
  }

  return endDate.diff(startDate, ['years', 'months', 'days']);
}

/**
 * Returns the number of months between two dates
 */
export function monthsBetweenDates(dateRange: DateRange): number;
export function monthsBetweenDates(startDate: DateTime, endDate: DateTime): number;
export function monthsBetweenDates(startParam: DateTime | DateRange, endParam?: DateTime): number {
  return Math.round(getDuration(startParam as DateTime, endParam!).as('months'));
}

export function yearsBetweenDates(dateRange: DateRange): number;
export function yearsBetweenDates(startDate: DateTime, endDate: DateTime): number;
export function yearsBetweenDates(startParam: DateTime | DateRange, endParam?: DateTime): number {
  return Math.round(getDuration(startParam as DateTime, endParam!).as('years'));
}
//#endregion
